import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

export type DotstormingTheme = {
  title: string | null;
  titleColor: string;
  titleBackgroundColor: string;
  yellowThemeColor: string;
  yellowThemeText: string;
  blueThemeColor: string;
  blueThemeText: string;
  redThemeColor: string;
  redThemeText: string;
  panelTitleColor: string;
  panelColor: string;
};

type ThemeFieldKey =
  | 'title'
  | 'titleColor'
  | 'titleBackgroundColor'
  | 'yellowThemeColor'
  | 'yellowThemeText'
  | 'blueThemeColor'
  | 'blueThemeText'
  | 'redThemeColor'
  | 'redThemeText'
  | 'panelTitleColor'
  | 'panelColor';

type ColorFieldKey = Exclude<ThemeFieldKey, 'title'>;
type ThemeEditorTab = 'header' | 'actions' | 'panels';
type EnabledThemeFields = Record<ColorFieldKey, boolean>;

export type ThemeEditorDialogProps = {
  open: boolean;
  onClose: () => void;
  loadTheme: () => Promise<DotstormingTheme>;
  saveTheme: (theme: DotstormingTheme) => Promise<DotstormingTheme>;
  title?: string;
  loadErrorMessage?: string;
  saveErrorMessage?: string;
  successMessage?: string;
};

const isHexColor = (value: string) => /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i.test(value);

const defaultThemeState: DotstormingTheme = {
  title: null,
  titleColor: '',
  titleBackgroundColor: '',
  yellowThemeColor: '',
  yellowThemeText: '',
  blueThemeColor: '',
  blueThemeText: '',
  redThemeColor: '',
  redThemeText: '',
  panelTitleColor: '',
  panelColor: '',
};

const labels: Record<ThemeFieldKey, string> = {
  title: 'Header title',
  titleColor: 'Title color',
  titleBackgroundColor: 'Title background color',
  yellowThemeColor: 'Yellow theme color',
  yellowThemeText: 'Yellow text color',
  blueThemeColor: 'Blue theme color',
  blueThemeText: 'Blue text color',
  redThemeColor: 'Red theme color',
  redThemeText: 'Red text color',
  panelTitleColor: 'Panel title color',
  panelColor: 'Panel color',
};

const defaultThemeValues: Record<ThemeFieldKey, string> = {
  title: 'dotstorming',
  titleColor: '#022B3A',
  titleBackgroundColor: '#FFFFFF',
  yellowThemeColor: '#ffd04b',
  yellowThemeText: '#ffffff',
  blueThemeColor: '#04CEFF',
  blueThemeText: '#ffffff',
  redThemeColor: '#c13d6c',
  redThemeText: '#ffffff',
  panelTitleColor: '#022b3a',
  panelColor: '#ffffff',
};

const colorFieldKeys: ColorFieldKey[] = [
  'titleColor',
  'titleBackgroundColor',
  'yellowThemeColor',
  'yellowThemeText',
  'blueThemeColor',
  'blueThemeText',
  'redThemeColor',
  'redThemeText',
  'panelTitleColor',
  'panelColor',
];

const headerColorFieldKeys: ColorFieldKey[] = ['titleColor', 'titleBackgroundColor'];

const actionColorFieldKeys: ColorFieldKey[] = [
  'yellowThemeColor',
  'yellowThemeText',
  'blueThemeColor',
  'blueThemeText',
  'redThemeColor',
  'redThemeText',
];

const panelColorFieldKeys: ColorFieldKey[] = ['panelTitleColor', 'panelColor'];

const themeEditorTabs: { label: string; value: ThemeEditorTab }[] = [
  { label: 'Header', value: 'header' },
  { label: 'Actions', value: 'actions' },
  { label: 'Panels', value: 'panels' },
];

export const ThemeEditorDialog = ({
  open,
  onClose,
  loadTheme,
  saveTheme,
  title = 'Edit Theme',
  loadErrorMessage = 'Unable to load theme settings.',
  saveErrorMessage = 'Unable to save theme settings.',
  successMessage = 'Theme updated successfully.',
}: ThemeEditorDialogProps) => {
  const [enabled, setEnabled] = useState<EnabledThemeFields>({
    titleColor: false,
    titleBackgroundColor: false,
    yellowThemeColor: false,
    yellowThemeText: false,
    blueThemeColor: false,
    blueThemeText: false,
    redThemeColor: false,
    redThemeText: false,
    panelTitleColor: false,
    panelColor: false,
  });
  const [values, setValues] = useState<DotstormingTheme>(defaultThemeState);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [savedMessage, setSavedMessage] = useState('');
  const [activeTab, setActiveTab] = useState<ThemeEditorTab>('header');
  const isHeaderTitleEnabled = values.title !== null;

  const setColorFieldEnabled = (key: ColorFieldKey, value: boolean) => {
    setEnabled((current) => ({ ...current, [key]: value }));

    if (value) {
      setValues((current) => {
        const currentValue = current[key];
        if (typeof currentValue === 'string' && currentValue.trim().length > 0) {
          return current;
        }

        return {
          ...current,
          [key]: defaultThemeValues[key],
        };
      });
    }
  };

  const setHeaderTitleEnabled = (value: boolean) => {
    setValues((current) => {
      if (!value) {
        return { ...current, title: null };
      }

      const currentTitle = current.title?.trim();
      return {
        ...current,
        title: currentTitle || defaultThemeValues.title,
      };
    });
  };

  const setFieldValue = (key: ThemeFieldKey, value: string | null) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const getSanitizedColor = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }

    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSavedMessage('');
    setActiveTab('header');

    void loadTheme()
      .then((theme) => {
        setValues(theme);
        setEnabled({
          titleColor: Boolean(theme.titleColor),
          titleBackgroundColor: Boolean(theme.titleBackgroundColor),
          yellowThemeColor: Boolean(theme.yellowThemeColor),
          yellowThemeText: Boolean(theme.yellowThemeText),
          blueThemeColor: Boolean(theme.blueThemeColor),
          blueThemeText: Boolean(theme.blueThemeText),
          redThemeColor: Boolean(theme.redThemeColor),
          redThemeText: Boolean(theme.redThemeText),
          panelTitleColor: Boolean(theme.panelTitleColor),
          panelColor: Boolean(theme.panelColor),
        });
      })
      .catch(() => {
        setErrorMessage(loadErrorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [loadErrorMessage, loadTheme, open]);

  const previewColors = useMemo(
    () => ({
      titleColor: enabled.titleColor ? getSanitizedColor(values.titleColor) : '',
      titleBackgroundColor: enabled.titleBackgroundColor
        ? getSanitizedColor(values.titleBackgroundColor)
        : '',
      yellowThemeColor: enabled.yellowThemeColor ? getSanitizedColor(values.yellowThemeColor) : '',
      yellowThemeText: enabled.yellowThemeText ? getSanitizedColor(values.yellowThemeText) : '',
      blueThemeColor: enabled.blueThemeColor ? getSanitizedColor(values.blueThemeColor) : '',
      blueThemeText: enabled.blueThemeText ? getSanitizedColor(values.blueThemeText) : '',
      redThemeColor: enabled.redThemeColor ? getSanitizedColor(values.redThemeColor) : '',
      redThemeText: enabled.redThemeText ? getSanitizedColor(values.redThemeText) : '',
      panelTitleColor: enabled.panelTitleColor ? getSanitizedColor(values.panelTitleColor) : '',
      panelColor: enabled.panelColor ? getSanitizedColor(values.panelColor) : '',
    }),
    [enabled, values]
  );

  const onSave = async () => {
    setErrorMessage('');
    setSavedMessage('');

    const nextTheme: DotstormingTheme = {
      title: values.title === null ? null : values.title.trim(),
      titleColor: enabled.titleColor ? getSanitizedColor(values.titleColor) : '',
      titleBackgroundColor: enabled.titleBackgroundColor
        ? getSanitizedColor(values.titleBackgroundColor)
        : '',
      yellowThemeColor: enabled.yellowThemeColor ? getSanitizedColor(values.yellowThemeColor) : '',
      yellowThemeText: enabled.yellowThemeText ? getSanitizedColor(values.yellowThemeText) : '',
      blueThemeColor: enabled.blueThemeColor ? getSanitizedColor(values.blueThemeColor) : '',
      blueThemeText: enabled.blueThemeText ? getSanitizedColor(values.blueThemeText) : '',
      redThemeColor: enabled.redThemeColor ? getSanitizedColor(values.redThemeColor) : '',
      redThemeText: enabled.redThemeText ? getSanitizedColor(values.redThemeText) : '',
      panelTitleColor: enabled.panelTitleColor ? getSanitizedColor(values.panelTitleColor) : '',
      panelColor: enabled.panelColor ? getSanitizedColor(values.panelColor) : '',
    };

    for (const key of colorFieldKeys) {
      const color = nextTheme[key];
      if (color && !isHexColor(color)) {
        setErrorMessage(`A valid hex color is required for "${labels[key]}".`);
        return;
      }
    }

    setIsSaving(true);

    try {
      const savedTheme = await saveTheme(nextTheme);
      setValues(savedTheme);
      setSavedMessage(successMessage);
      onClose();
    } catch {
      setErrorMessage(saveErrorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const renderColorField = (key: ColorFieldKey) => {
    const previewValue = previewColors[key] || defaultThemeValues[key];

    return (
      <Stack
        key={key}
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'center' }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 240 }}>
          <Switch
            slotProps={{ input: { 'aria-label': labels[key] } }}
            checked={enabled[key]}
            onChange={(event) => setColorFieldEnabled(key, event.target.checked)}
          />
          <Typography>{labels[key]}</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
          <TextField
            fullWidth
            placeholder={defaultThemeValues[key]}
            disabled={!enabled[key] || isLoading}
            value={values[key] ?? ''}
            onChange={(event) => setFieldValue(key, event.target.value)}
          />
          <Box
            component="input"
            type="color"
            disabled={!enabled[key] || isLoading}
            value={previewValue}
            onChange={(event) => setFieldValue(key, event.target.value)}
            sx={{
              width: 44,
              height: 44,
              border: '1px solid #d9d9d9',
              borderRadius: 1,
              p: 0,
              bgcolor: 'transparent',
            }}
          />
        </Stack>
      </Stack>
    );
  };

  const renderTabPanel = (tab: ThemeEditorTab, children: ReactNode) => (
    <Box
      role="tabpanel"
      hidden={activeTab !== tab}
      id={`theme-editor-${tab}-panel`}
      aria-labelledby={`theme-editor-${tab}-tab`}
    >
      {activeTab === tab ? <Stack spacing={2}>{children}</Stack> : null}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          {savedMessage ? <Alert severity="success">{savedMessage}</Alert> : null}

          <Tabs
            value={activeTab}
            onChange={(_event, nextTab: ThemeEditorTab) => setActiveTab(nextTab)}
            aria-label="Theme sections"
            variant="scrollable"
            allowScrollButtonsMobile
          >
            {themeEditorTabs.map((tab) => (
              <Tab
                key={tab.value}
                id={`theme-editor-${tab.value}-tab`}
                aria-controls={`theme-editor-${tab.value}-panel`}
                label={tab.label}
                value={tab.value}
              />
            ))}
          </Tabs>

          {renderTabPanel(
            'header',
            <>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ md: 'center' }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 240 }}>
                  <Switch
                    slotProps={{ input: { 'aria-label': labels.title } }}
                    checked={isHeaderTitleEnabled}
                    onChange={(event) => setHeaderTitleEnabled(event.target.checked)}
                  />
                  <Typography>{labels.title}</Typography>
                </Stack>
                <TextField
                  fullWidth
                  disabled={!isHeaderTitleEnabled || isLoading}
                  value={values.title ?? ''}
                  placeholder={defaultThemeValues.title}
                  onChange={(event) => setFieldValue('title', event.target.value)}
                />
              </Stack>
              {headerColorFieldKeys.map(renderColorField)}
            </>
          )}

          {renderTabPanel('actions', actionColorFieldKeys.map(renderColorField))}

          {renderTabPanel('panels', panelColorFieldKeys.map(renderColorField))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={() => void onSave()} disabled={isSaving || isLoading} variant="contained">
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
